import * as d3 from 'd3';
import { AutomationFootprintTerminal, AutomationFootprintShape } from '@prism/prism-shared';

type G = d3.Selection<SVGGElement, unknown, null, undefined>;

// Anything with footprint drawing geometry can be rendered (e.g. an AutomationFootprintVersion).
export interface FootprintSymbolSource {
    shapes: AutomationFootprintShape[];
    terminals: AutomationFootprintTerminal[];
    width: number;
    height: number;
}

const S_STROKE = 'var(--color-5, #fff)';
const S_SW = 2;
const PIN_COLORS_MAP: Record<string, string> = {
    electrical: '#ffd600', // yellow
    pneumatic: '#29b6f6',  // sky blue (compressed air)
    data: '#66bb6a',       // green (signal/data)
};

/**
 * Renders a footprint's shapes + pin stubs into the given d3 <g>. Mirrors the schematic editor's
 * symbol renderer so footprints look identical wherever they appear (editor library, browse cards,
 * footprint view).
 */
export function renderFootprintSymbol(
    g: G,
    def: FootprintSymbolSource,
    options: { coloredPins?: boolean; showPinLabels?: boolean } = {},
): void {
    for (const shape of (def.shapes ?? [])) {
        const stroke = shape.stroke ?? S_STROKE;
        const fill = shape.fill ?? 'none';
        const sw = shape.strokeWidth ?? S_SW;
        switch (shape.type) {
            case 'rect':
                g.append('rect')
                    .attr('x', shape.x ?? 0).attr('y', shape.y ?? 0)
                    .attr('width', shape.width ?? 0).attr('height', shape.height ?? 0)
                    .attr('stroke', stroke).attr('stroke-width', sw).attr('fill', fill);
                break;
            case 'circle':
                g.append('circle')
                    .attr('cx', shape.cx ?? 0).attr('cy', shape.cy ?? 0).attr('r', shape.r ?? 0)
                    .attr('stroke', stroke).attr('stroke-width', sw).attr('fill', fill);
                break;
            case 'line': {
                const line = g.append('line')
                    .attr('x1', shape.x1 ?? 0).attr('y1', shape.y1 ?? 0)
                    .attr('x2', shape.x2 ?? 0).attr('y2', shape.y2 ?? 0)
                    .attr('stroke', stroke).attr('stroke-width', sw);
                if (shape.dashed) {
                    line.attr('stroke-dasharray', '10 10');
                }
                break;
            }
            case 'arc': {
                const cx = shape.cx ?? 0;
                const cy = shape.cy ?? 0;
                const r = shape.r ?? 0;
                const sa = shape.startAngle ?? 0;
                const ea = shape.endAngle ?? Math.PI;
                const x1 = cx + r * Math.cos(sa);
                const y1 = cy + r * Math.sin(sa);
                const x2 = cx + r * Math.cos(ea);
                const y2 = cy + r * Math.sin(ea);
                const visualSweep = ((ea - sa) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
                const largeArc = visualSweep > Math.PI ? 1 : 0;
                g.append('path')
                    .attr('d', `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`)
                    .attr('stroke', stroke).attr('stroke-width', sw).attr('fill', fill);
                break;
            }
            case 'triangle': {
                const tx = shape.x ?? 0, ty = shape.y ?? 0, tw = shape.width ?? 0, th = shape.height ?? 0;
                g.append('polygon')
                    .attr('points', `${tx},${ty} ${tx},${ty + th} ${tx + tw},${ty + th / 2}`)
                    .attr('stroke', stroke).attr('stroke-width', sw).attr('fill', fill);
                break;
            }
            case 'textbox': {
                const tbx = shape.x ?? 0, tby = shape.y ?? 0, tbw = shape.width ?? 60, tbh = shape.height ?? 20;
                const anchor = shape.textAnchor ?? 'start';
                const textX = anchor === 'start' ? tbx + 2 : anchor === 'end' ? tbx + tbw - 2 : tbx + tbw / 2;
                g.append('text')
                    .attr('x', textX).attr('y', tby + tbh / 2)
                    .attr('text-anchor', anchor).attr('dominant-baseline', 'central')
                    .attr('fill', shape.stroke ?? S_STROKE)
                    .attr('font-size', shape.fontSize ?? 12).attr('font-family', 'monospace')
                    .text(shape.text ?? '');
                break;
            }
        }
    }

    const STUB = 15;
    for (const term of (def.terminals ?? [])) {
        const pinColor = options.coloredPins ? (PIN_COLORS_MAP[term.type] ?? S_STROKE) : S_STROKE;
        const dx = term.direction === 'left' ? -1 : term.direction === 'right' ? 1 : 0;
        const dy = term.direction === 'up' ? -1 : term.direction === 'down' ? 1 : 0;
        const ix = term.x + dx * STUB;
        const iy = term.y + dy * STUB;
        g.append('line')
            .attr('x1', term.x).attr('y1', term.y)
            .attr('x2', ix).attr('y2', iy)
            .attr('stroke', pinColor).attr('stroke-width', 2);
        g.append('circle')
            .attr('cx', term.x).attr('cy', term.y).attr('r', 3)
            .attr('fill', 'none').attr('stroke', pinColor).attr('stroke-width', 1.5);

        // Pin name, placed OUTSIDE the body just past the stub end so it sits in open space and never
        // overlaps the symbol's internal lines/shapes (it reads outward, away from the part). Opt-in so
        // dense contexts (browse cards, list previews) stay uncluttered; used where the pinout matters,
        // e.g. the read-only symbol preview dialog.
        if (options.showPinLabels && term.label) {
            const LABEL_GAP = 3;
            const isVertical = dy !== 0;
            if (isVertical) {
                const lx = term.x;
                const ly = iy + dy * LABEL_GAP;
                // Rotated upright; anchor so the text grows outward (down for a down pin, up for an up pin).
                const anchor = dy > 0 ? 'end' : 'start';
                g.append('text')
                    .attr('x', lx).attr('y', ly)
                    .attr('text-anchor', anchor)
                    .attr('dominant-baseline', 'central')
                    .attr('fill', pinColor)
                    .attr('font-size', 10).attr('font-family', 'monospace')
                    .attr('transform', `rotate(-90, ${lx}, ${ly})`)
                    .text(term.label);
            } else {
                const lx = ix + dx * LABEL_GAP;
                const ly = term.y;
                // Grows outward: rightward for a right pin, leftward for a left pin.
                const anchor = dx > 0 ? 'start' : 'end';
                g.append('text')
                    .attr('x', lx).attr('y', ly)
                    .attr('text-anchor', anchor)
                    .attr('dominant-baseline', 'central')
                    .attr('fill', pinColor)
                    .attr('font-size', 10).attr('font-family', 'monospace')
                    .text(term.label);
            }
        }
    }
}
